(() => {
  const productLocale = () => window.theme?.locales?.product || {}
  const cartLocale = () => window.theme?.locales?.cart || {}
  const formatMoney = cents => window.Shopify.formatMoney(cents, window.Shopify.moneyFormat)

  // Liquid'deki snippets/cm-stock-status.liquid ile aynı kuralları uygular.
  const stockLevel = (variant, inventory, threshold) => {
    if (!variant?.available) return 'sold_out'
    if (!inventory?.tracked || typeof inventory.quantity !== 'number') return 'in_stock'
    if (inventory.quantity === 1) return 'single'
    if (inventory.quantity > 1 && inventory.quantity <= threshold) return 'limited'
    return 'in_stock'
  }

  const stockLabel = level => productLocale().stock_status?.[level] || ''

  class CommuniteProductForm extends HTMLElement {
    connectedCallback () {
      this.controller?.abort()
      this.controller = new AbortController()
      this.form = this.querySelector('form')
      if (!this.form) return

      this.variants = this.getVariants()
      this.inventory = this.getInventory()
      this.limitedStockThreshold = Number(
        this.closest('[data-cm-product-root]')?.dataset.cmLimitedStockThreshold || 0
      )
      const { signal } = this.controller

      this.form.addEventListener('submit', event => this.onSubmit(event), { signal })
      this.form.querySelectorAll('[data-cm-product-option]').forEach(control => {
        control.addEventListener('change', () => this.onVariantChange(), { signal })
      })
      this.form.querySelectorAll('[data-cm-quantity-minus], [data-cm-quantity-plus]').forEach(button => {
        button.addEventListener('click', event => this.onQuantityClick(event), { signal })
      })
      this.form.querySelector('[data-cm-buy-button]')?.addEventListener('click', () => this.onBuyNow(), { signal })
    }

    disconnectedCallback () {
      this.controller?.abort()
    }

    getVariants () {
      const script = this.querySelector('[data-cm-product-variants]')
      if (!script) return []

      try {
        return JSON.parse(script.textContent)
      } catch (error) {
        return []
      }
    }

    getInventory () {
      const script = this.querySelector('[data-cm-product-inventory]')
      if (!script) return new Map()

      try {
        return new Map(JSON.parse(script.textContent).map(item => [Number(item.id), item]))
      } catch (error) {
        return new Map()
      }
    }

    getSelectedOptions () {
      return [...this.form.querySelectorAll('[data-cm-product-option-group]')].map(group => {
        const select = group.querySelector('select[data-cm-product-option]')
        if (select) return select.value
        return group.querySelector('input[data-cm-product-option]:checked')?.value
      })
    }

    getCurrentVariant () {
      const id = Number(this.form.querySelector('[data-cm-variant-id]')?.value)
      return this.variants.find(variant => Number(variant.id) === id)
    }

    onVariantChange () {
      const selectedOptions = this.getSelectedOptions()
      const variant = this.variants.find(item =>
        item.options.every((option, index) => option === selectedOptions[index])
      )

      this.form.querySelectorAll('[data-cm-product-option-group]').forEach(group => {
        const value = group.querySelector('select')?.value ||
          group.querySelector('input[data-cm-product-option]:checked')?.value
        const output = group.querySelector('[data-cm-option-value]')
        if (output) output.textContent = value || ''
      })

      this.updateVariant(variant)
    }

    updateVariant (variant) {
      const variantInput = this.form.querySelector('[data-cm-variant-id]')
      if (variantInput) variantInput.value = variant?.id || ''

      this.updatePrice(variant)
      this.updateAvailability(variant)
      this.updateRefurbishedStock(variant)
      this.clearStatus()

      if (variant && this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href)
        url.searchParams.set('variant', variant.id)
        window.history.replaceState({}, '', url)
      }

      this.closest('[data-cm-product-root]')?.dispatchEvent(new CustomEvent('cm:product:variant-change', {
        bubbles: true,
        detail: { variant }
      }))
      window.dispatchEvent(new CustomEvent('kt.product.variant.change', { detail: variant }))
    }

    updatePrice (variant) {
      if (!variant) return

      const finalPrice = this.form.querySelector('[data-cm-final-price]')
      const comparePrice = this.form.querySelector('[data-cm-compare-price]')
      const saleBadge = this.form.querySelector('[data-cm-sale-badge]')
      const stickyPrice = this.form.querySelector('[data-cm-sticky-price]')
      const isSale = variant.compare_at_price > variant.price

      if (finalPrice) {
        finalPrice.textContent = formatMoney(variant.price)
        finalPrice.classList.toggle('product-price-final-sale', isSale)
        finalPrice.setAttribute('aria-label', isSale
          ? `${productLocale().sale_price}: ${formatMoney(variant.price)}`
          : formatMoney(variant.price))
      }

      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price)

      if (comparePrice) {
        comparePrice.hidden = !isSale
        comparePrice.textContent = isSale ? formatMoney(variant.compare_at_price) : ''
      }

      if (saleBadge) {
        saleBadge.hidden = !isSale

        if (isSale) {
          const saving = saleBadge.dataset.discountType === 'percentage'
            ? `${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)}%`
            : formatMoney(variant.compare_at_price - variant.price)
          saleBadge.textContent = `${productLocale().save} ${saving}`
        }
      }
    }

    updateAvailability (variant) {
      const available = Boolean(variant?.available)
      const label = !variant
        ? productLocale().unavailable
        : available
          ? productLocale().add_to_cart
          : productLocale().sold_out

      this.form.querySelectorAll('[data-cm-add-button]').forEach(button => {
        button.disabled = !available
        button.textContent = label
      })

      const buyButton = this.form.querySelector('[data-cm-buy-button]')
      if (buyButton) buyButton.disabled = !available

      const soldOutBadge = this.form.querySelector('[data-cm-sold-out-badge]')
      if (soldOutBadge) soldOutBadge.hidden = available || !variant

      const level = stockLevel(variant, this.inventory.get(Number(variant?.id)), this.limitedStockThreshold)

      this.form.querySelectorAll('[data-cm-stock]').forEach(status => {
        status.dataset.cmStockLevel = level
        status.className = status.className.replace(/cm-stock--[a-z_]+/, `cm-stock--${level}`)
        const text = status.querySelector('[data-cm-stock-text]')
        if (text) text.textContent = variant ? stockLabel(level) : productLocale().unavailable
      })
    }

    updateRefurbishedStock (variant) {
      const stock = this.closest('[data-cm-product-root]')?.querySelector('[data-cm-refurb-stock]')
      const label = stock?.querySelector('[data-cm-refurb-stock-label]')
      if (!stock || !label) return

      const inventory = this.inventory.get(Number(variant?.id))
      const tracked = Boolean(inventory?.tracked)
      stock.dataset.inventoryTracked = String(tracked)
      label.textContent = stockLabel(stockLevel(variant, inventory, this.limitedStockThreshold))
    }

    onQuantityClick (event) {
      const input = event.currentTarget.closest('[data-cm-quantity]')?.querySelector('input[name="quantity"]')
      if (!input) return

      const increment = event.currentTarget.matches('[data-cm-quantity-plus]')
      const minimum = Number(input.min || 1)
      const nextValue = Number(input.value || minimum) + (increment ? 1 : -1)
      input.value = Math.max(minimum, nextValue)
    }

    async onSubmit (event) {
      event.preventDefault()
      const variant = this.variants.length ? this.getCurrentVariant() : null
      if (this.variants.length && !variant?.available) return

      this.clearStatus()
      this.setBusy(true)

      try {
        const formData = new FormData(this.form)
        const cartContainer = document.querySelector('#offcanvas-cart cart-container') ||
          document.querySelector('cart-container')

        if (cartContainer?.add) {
          await cartContainer.add(formData)
        } else if (window.CommuniteCart?.add) {
          await window.CommuniteCart.add(formData)
          window.CommuniteCart.showDrawer()
        } else {
          throw new Error(cartLocale().error || 'Cart could not be updated. Please try again.')
        }
      } catch (error) {
        this.showStatus(error.message)
      } finally {
        this.setBusy(false)
      }
    }

    async onBuyNow () {
      const formData = new FormData(this.form)
      const variantId = formData.get('id')
      const quantity = Math.max(1, Number(this.form.querySelector('[name="quantity"]')?.value || 1))
      const sellingPlan = formData.get('selling_plan')
      const variant = this.variants.length ? this.getCurrentVariant() : null
      if (!variantId) return

      if (variant?.requires_selling_plan && !sellingPlan) {
        this.showStatus(productLocale().selling_plan_required || productLocale().unavailable)
        return
      }

      if (sellingPlan) {
        this.clearStatus()
        this.setBusy(true)
        formData.set('quantity', String(quantity))

        try {
          if (typeof window.CommuniteCart?.add !== 'function') {
            throw new Error(cartLocale().error || 'Cart could not be updated. Please try again.')
          }

          await window.CommuniteCart.add(formData)
          const root = new URL(window.Shopify.routes.root || '/', window.location.origin)
          window.location.assign(new URL('checkout', root))
        } catch (error) {
          this.showStatus(error.message)
        } finally {
          if (this.isConnected) this.setBusy(false)
        }
        return
      }

      window.location.href = `${window.Shopify.routes.cart_url}/${variantId}:${quantity}`
    }

    setBusy (busy) {
      const variant = this.variants.length ? this.getCurrentVariant() : null
      const shouldDisable = busy || (this.variants.length && !variant?.available)

      this.form.querySelectorAll('[data-cm-add-button]').forEach(button => {
        button.disabled = shouldDisable
        button.setAttribute('aria-busy', String(busy))
      })

      const buyButton = this.form.querySelector('[data-cm-buy-button]')
      if (buyButton) {
        buyButton.disabled = shouldDisable
        buyButton.setAttribute('aria-busy', String(busy))
      }
    }

    clearStatus () {
      const status = this.form.querySelector('[data-cm-product-status]')
      if (status) status.hidden = true
    }

    showStatus (message) {
      const status = this.form.querySelector('[data-cm-product-status]')
      if (!status) return
      status.textContent = message || cartLocale().error
      status.hidden = false
    }
  }

  if (!customElements.get('cm-product-form')) {
    customElements.define('cm-product-form', CommuniteProductForm)
  }

  class CommuniteProductMediaGallery extends HTMLElement {
    connectedCallback () {
      this.destroy()
      if (!window.Swiper) return

      const thumbsElement = this.querySelector('.swiper-thumbs')
      const mainElement = this.querySelector('.swiper-main')
      if (!mainElement) return

      this.thumbs = thumbsElement
        ? new window.Swiper(thumbsElement, {
          slidesPerView: Math.max(3, Number(this.dataset.thumbsPerView) || 5),
          spaceBetween: 10,
          watchSlidesProgress: true
        })
        : null

      this.main = new window.Swiper(mainElement, {
        speed: Number(this.dataset.speed) || 300,
        rewind: true,
        watchOverflow: true,
        pagination: {
          el: this.querySelector('.swiper-pagination'),
          type: this.dataset.paginationType === 'bullets' ? 'bullets' : 'fraction'
        },
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev')
        },
        scrollbar: this.querySelector('.swiper-scrollbar')
          ? {
            el: this.querySelector('.swiper-scrollbar'),
            draggable: true
          }
          : undefined,
        thumbs: this.thumbs ? { swiper: this.thumbs } : undefined
      })

      this.main.slideTo(Number(this.dataset.initialSlide) || 0, 0)
      this.root = this.closest('[data-cm-product-root]')
      this.controller = new AbortController()
      this.root?.addEventListener('cm:product:variant-change', event => {
        const position = event.detail.variant?.featured_media?.position
        if (position) this.main?.slideTo(position - 1)
      }, { signal: this.controller.signal })
    }

    disconnectedCallback () {
      this.destroy()
    }

    destroy () {
      this.controller?.abort()
      this.main?.destroy(true, true)
      this.thumbs?.destroy(true, true)
      this.main = null
      this.thumbs = null
    }
  }

  if (!customElements.get('cm-product-media-gallery')) {
    customElements.define('cm-product-media-gallery', CommuniteProductMediaGallery)
  }

  // Paylaşım: Web Share API varsa yerel paylaşım sayfası, yoksa panoya kopyalama.
  const initShare = root => {
    root.querySelectorAll('[data-cm-share]').forEach(share => {
      if (share.dataset.cmShareInitialized === 'true') return
      share.dataset.cmShareInitialized = 'true'

      const button = share.querySelector('[data-cm-share-copy]')
      const label = share.querySelector('[data-cm-share-copy-label]')
      const status = share.querySelector('[data-cm-share-status]')
      if (!button) return

      const url = share.dataset.cmShareUrl || window.location.href
      const title = share.dataset.cmShareTitle || document.title

      button.addEventListener('click', async () => {
        if (navigator.share) {
          try {
            await navigator.share({ title, url })
            return
          } catch (error) {
            if (error?.name === 'AbortError') return
          }
        }

        try {
          await navigator.clipboard.writeText(url)
        } catch (error) {
          window.prompt(label?.textContent?.trim() || '', url)
          return
        }

        const copied = productLocale().share?.copied
        if (status && copied) {
          status.textContent = copied
          status.classList.remove('cm-u-visually-hidden')
          window.setTimeout(() => {
            status.textContent = ''
            status.classList.add('cm-u-visually-hidden')
          }, 4000)
        }
      })
    })
  }

  initShare(document)
  document.addEventListener('shopify:section:load', event => initShare(event.target))

  const adjustAppBlocks = root => {
    root.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
      radio.classList.add('form-check-input')
    })
  }

  adjustAppBlocks(document)
  document.addEventListener('shopify:section:load', event => adjustAppBlocks(event.target))
  window.addEventListener('kt.product.quick_view.modal_shown', () => adjustAppBlocks(document))
})()
